import { useEffect, useState } from 'react';
import { inpatientsServices } from '../services/endpoints/inpatients';

interface UseInpatientHubDataParams {
  investigation: { uuid: string };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
}

export const useInpatientHubData = ({
  investigation,
  departmentSelected,
  startDate,
  endDate,
}: UseInpatientHubDataParams) => {
  const [currentSituation, setCurrentSituation] = useState<any>(null);
  const [periodActivity, setPeriodActivity] = useState<any>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    const uuidDepartment = departmentSelected || 'all';

    if (!investigation?.uuid) {
      return undefined;
    }

    setLoadingCurrent(true);
    inpatientsServices.getCurrentSituation(
      investigation.uuid,
      uuidDepartment,
      abortController.signal,
    )
      .then((data) => {
        if (!abortController.signal.aborted) {
          setCurrentSituation(data);
        }
      })
      .catch((err) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Failed to load current inpatient situation'));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoadingCurrent(false);
        }
      });

    return () => abortController.abort();
  }, [investigation?.uuid, departmentSelected]);

  useEffect(() => {
    const abortController = new AbortController();
    const uuidDepartment = departmentSelected || 'all';

    if (!investigation?.uuid || !startDate || !endDate) {
      return undefined;
    }

    setLoadingPeriod(true);
    inpatientsServices.getPeriodActivity(
      investigation.uuid,
      uuidDepartment,
      startDate,
      endDate,
      abortController.signal,
    )
      .then((data) => {
        if (!abortController.signal.aborted) {
          setPeriodActivity(data);
        }
      })
      .catch((err) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Failed to load inpatient period activity'));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoadingPeriod(false);
        }
      });

    return () => abortController.abort();
  }, [investigation?.uuid, departmentSelected, startDate, endDate]);

  return {
    currentSituation,
    periodActivity,
    loadingCurrent,
    loadingPeriod,
    error,
  };
};
